import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowElementProps, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_GASTRONOMY_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import { propertyOwnerGastronomyRentSchema } from './propertyOwnerGastronomyRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import CommercialCostsRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/commercial/CommercialCostsRent'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  estateFeatures,
  interiorQuality,
  numberOfBeds,
  numberOfSeats,
  parkingSpace,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  auxiliaryAreaField,
  mainFloorSpaceField,
  priceField,
  rentPriceOnRequestToggle,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { buildingTypeField } from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'

export const gastronomyRentBasicInfoScreen = {
  screen: SCREENS[SCREEN_BASIC_INFO],
  element: (props: FlowElementProps) => (
    <BasicInfo
      {...props}
      screenTooltipKey='tooltip.screens.basicInfo.gastronomyRentSell'
      properties={[
        buildingTypeField(
          'object',
          [
            BuildingTypeOption.BAR_LOUNGE,
            BuildingTypeOption.CAFE,
            BuildingTypeOption.GUESTS_HOUSE,
            BuildingTypeOption.TAVERN,
            BuildingTypeOption.HOTEL,
            BuildingTypeOption.HOTEL_RESIDENCE,
            BuildingTypeOption.HOTEL_GARNI,
            BuildingTypeOption.PENSION,
            BuildingTypeOption.RESTAURANT,
            BuildingTypeOption.BUNGALOW,
          ],
          getDefaultClasses('full')
        ),
        totalFloorSpaceField(),
        mainFloorSpaceField(MainFloorType.GASTRONOMY_AREA, 'right', undefined),
        auxiliaryAreaField(),
        priceField(PriceFieldType.RENT_PRICE, getDefaultClasses('leftOnAll')),
        rentPriceOnRequestToggle(),
      ]}
    />
  ),
}
export const gastronomyRentOtherFacilitiesScreen = {
  screen: SCREENS[SCREEN_OTHER_FACILITIES],
  element: (props: FlowElementProps) => (
    <OtherFacilities
      {...props}
      screenTooltipKey='tooltip.screens.otherFacilities.allRent'
      properties={[
        estateFeatures([
          RealEstateFeatures.BASEMENT,
          RealEstateFeatures.LISTED,
          RealEstateFeatures.PERSON_ELEVATOR,
          RealEstateFeatures.TERRACE,
        ]),
        interiorQuality(),
        numberOfSeats(getDefaultClasses('left')),
        numberOfBeds(getDefaultClasses('right')),
        totalFloorsField(getDefaultClasses('left')),
        parkingSpace({ showParkingSpaceType: false }),
      ]}
    />
  ),
}
const propertyOwnerGastronomyRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  gastronomyRentBasicInfoScreen,
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <CommercialCostsRent {...props} hasMoveInDate={true} screenTooltip='tooltip.screens.costs.allRent' />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => (
      <Description
        {...props}
        hasFurnishingNote={true}
        hasLocationNote={true}
        hasOtherNote={true}
        hasHomeStreetHome={true}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  gastronomyRentOtherFacilitiesScreen,
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerGastronomyRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.gastronomy',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_GASTRONOMY_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_GASTRONOMY_RENT,
    validationSchema: propertyOwnerGastronomyRentSchema,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.GASTRONOMY,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
  },
  flowScreens: propertyOwnerGastronomyRentFlowScreens,
}
